import {
  getOrganizationRepository,
  getOrgInvitationRepository,
  getSwitchOrganizationUseCase,
} from "@/infrastructure/di";
import { UseCaseError } from "@/application/errors/UseCaseError";
import type { CreateInvitationRequest } from "@/domain/invitation";
import type { UpdateOrganizationRequest } from "@/domain/organization";
import type { QueryKey } from "@tanstack/react-query";
import { InvalidationEntity } from "../entityDependencies";
import { queryKeys } from "../queryKeys";
import { useCreateMutation } from "../createMutation";

/** Reconciles only the organization sub-resources a mutation touched. */
const organizationParts = (orgId: string, keys: readonly QueryKey[]) => ({
  orgId,
  entities: [InvalidationEntity.ORGANIZATION],
  facets: [],
  keys: () => keys,
});

export const useSwitchOrganizationMutation = () =>
  useCreateMutation({
    mutationFn: async (organizationId: string | number) => {
      const organizationUuid = String(organizationId);
      const result = await getSwitchOrganizationUseCase().execute(organizationUuid);
      if (!result.success) {
        throw new UseCaseError(result.error ?? "Failed to switch organization");
      }
      return { organizationId: organizationUuid };
    },
    cache: (c) => c.none(),
    errorLogMessage: "Failed to switch organization:",
  });

export const useUpdateMemberRole = (orgId: string) =>
  useCreateMutation({
    mutationFn: ({ memberUuid, role }: { memberUuid: string; role: string }) =>
      getOrganizationRepository().updateMemberRole(memberUuid, role),
    cache: (c) => c.invalidate(organizationParts(orgId, [queryKeys.organization.users(orgId)])),
    errorLogMessage: "Failed to update member role:",
  });

export const useUpdateMemberStatus = (orgId: string) =>
  useCreateMutation({
    mutationFn: ({ memberUuid, is_active }: { memberUuid: string; is_active: boolean }) =>
      getOrganizationRepository().updateMemberStatus(memberUuid, is_active),
    cache: (c) => c.invalidate(organizationParts(orgId, [queryKeys.organization.users(orgId)])),
    errorLogMessage: "Failed to update member status:",
  });

export const useRemoveMember = (orgId: string) =>
  useCreateMutation({
    mutationFn: async (memberUuid: string) => {
      await getOrganizationRepository().removeMember(memberUuid);
      return memberUuid;
    },
    cache: (c) => c.invalidate(organizationParts(orgId, [queryKeys.organization.users(orgId)])),
    errorLogMessage: "Failed to remove member:",
  });

export const useOrgResendInvitation = (orgId: string) =>
  useCreateMutation({
    mutationFn: async (invitationUuid: string) => {
      await getOrganizationRepository().resendInvitation(invitationUuid);
      return invitationUuid;
    },
    cache: (c) =>
      c.invalidate(organizationParts(orgId, [queryKeys.organization.invitations(orgId)])),
    errorLogMessage: "Failed to resend invitation:",
  });

export const useOrgCancelInvitation = (orgId: string) =>
  useCreateMutation({
    mutationFn: async (invitationUuid: string) => {
      await getOrganizationRepository().cancelInvitation(invitationUuid);
      return invitationUuid;
    },
    cache: (c) =>
      c.invalidate(organizationParts(orgId, [queryKeys.organization.invitations(orgId)])),
    errorLogMessage: "Failed to cancel invitation:",
  });

export const useOrgUpdateOrganization = (orgId: string) =>
  useCreateMutation({
    mutationFn: (data: UpdateOrganizationRequest) =>
      getOrganizationRepository().updateOrganization(data),
    cache: (c) =>
      c.invalidate(
        organizationParts(orgId, [
          queryKeys.organization.details(orgId),
          queryKeys.organization.userOrganizations(),
        ]),
      ),
    errorLogMessage: "Failed to update organization:",
  });

export const useOrgCreateInvitation = (orgId: string) =>
  useCreateMutation({
    mutationFn: (data: CreateInvitationRequest) =>
      getOrgInvitationRepository().createInvitation(data),
    cache: (c) =>
      c.invalidate(organizationParts(orgId, [queryKeys.organization.invitations(orgId)])),
    errorLogMessage: "Failed to create invitation:",
  });
